import React from 'react';
import { motion } from 'framer-motion';
import { EyeIcon, RefreshCwIcon } from 'lucide-react';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';
const GOVERNANCE_DASHBOARD = 'https://cdn.magicpatterns.com/uploads/v2bsFdK9rTunjM5Necd8bH/Screenshot_2026-08-27_at_11.17.06.png';
const GOVERNANCE_DETAIL = 'https://cdn.magicpatterns.com/uploads/gWVy1f55H3F8D5zorepRFz/Screenshot_2026-08-27_at_11.17.15.png';
const METRICS = [{
  value: '12',
  label: 'Markets in view'
}, {
  value: '5',
  label: 'Products tracked'
}, {
  value: '49',
  label: 'Assessments'
}, {
  value: '25',
  label: 'Root causes captured'
}] as const;
const LEADERSHIP_INSIGHTS = ['Where the largest market and product gaps sit', 'Which issues are structural versus local', 'Where assessment coverage is incomplete', 'Which campaign theme the calendar should prioritise next'] as const;
export function SlideGovernanceView({}: SlideProps) {
  return <InteriorSlide surface data-id="element-222" data-mp="100wy3qvacnzl">
      <motion.header initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="mb-[clamp(1rem,1.7vw,1.6rem)] flex-shrink-0" data-id="element-223" data-mp="1016pnivzb37e">
        <p className="text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-224" data-mp="r19vj1qtlc4y">
          The Snak format &amp; data insights
        </p>
        <h2 className="mt-3 max-w-6xl text-[clamp(2rem,3.5vw,3.35rem)] font-extrabold leading-[1.02] tracking-[-0.04em] text-[var(--fg)]" data-id="element-225" data-mp="1ir699q91vq3f">
          One governance view across{' '}
          <span className="text-[var(--brand)]" data-id="element-226" data-mp="1es6znscdj55n">brands, markets and cadence.</span>
        </h2>
        <p className="mt-3 max-w-4xl text-[clamp(0.9rem,1.15vw,1.05rem)] leading-relaxed text-[var(--muted)]" data-id="element-227" data-mp="19wrml2m6xdaa">
          Directors see the whole portfolio; CX teams retain the market and campaign
          detail needed to act.
        </p>
      </motion.header>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-5 lg:grid-cols-12" data-id="element-228" data-mp="1xnx21go7y3k2">
        <motion.section initial={{
        opacity: 0,
        y: 18
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.16,
        duration: 0.6
      }} className="flex min-h-0 flex-col gap-3 lg:col-span-8" aria-label="Brand intelligence dashboard" data-id="element-229" data-mp="2gknqjmwy3i30">
          <figure className="min-h-0 flex-1 overflow-hidden rounded-2xl border border-black/10 bg-white shadow-sm" data-id="element-230" data-mp="2btp5ka10hvtf">
            <img src={GOVERNANCE_DASHBOARD} alt="Brand intelligence governance dashboard showing markets, products, assessments, root causes, market gaps, and outliers" className="h-full w-full object-contain" data-id="element-231" data-mp="1xteeb5vsif7q" />
          </figure>
          <div className="grid flex-shrink-0 grid-cols-5 gap-3" data-id="element-232" data-mp="1suth7fbu4bni">
            <figure className="col-span-3 h-[clamp(3.5rem,7vw,6.5rem)] overflow-hidden rounded-xl border border-black/10 bg-white shadow-sm" data-id="element-233" data-mp="1sx63u5sjfa6w">
              <img src={GOVERNANCE_DETAIL} alt="Continuation of the market gap dashboard with regional comparisons" className="h-full w-full object-contain object-left" data-id="element-234" data-mp="3n8cjf2gt8if5" />
            </figure>
            <div className="col-span-2 flex flex-col justify-center rounded-xl border border-[var(--brand)]/10 bg-white px-4 py-3" data-id="element-235" data-mp="17im5tgwdrmlx">
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[var(--brand)]" data-id="element-236" data-mp="1gklpkgys54sk">
                Computed, never editorial
              </p>
              <p className="mt-1 text-xs font-semibold leading-snug text-[var(--fg)]" data-id="element-237" data-mp="1vc47ikb799ek">
                Outliers and regional gaps are derived from submitted data.
              </p>
            </div>
          </div>
        </motion.section>

        <motion.aside initial={{
        opacity: 0,
        y: 18
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.28,
        duration: 0.6
      }} className="flex min-h-0 flex-col gap-3 lg:col-span-4" data-id="element-238" data-mp="1zb39bw1mwq1s">
          <div className="grid grid-cols-2 gap-3" data-id="element-239" data-mp="4z521zmvy48j">
            {METRICS.map(metric => <article key={metric.label} className="rounded-2xl border border-[var(--brand)]/10 bg-white px-4 py-3 shadow-sm" data-id="element-240" data-mp="7bh0wma1k21o">
                <p className="text-[clamp(1.75rem,3vw,2.75rem)] font-extrabold leading-none text-[var(--fg)]" data-id="element-241" data-mp="gcnm38nmy119">
                  {metric.value}
                </p>
                <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.1em] text-[var(--muted)]" data-id="element-242" data-mp="l9no98hd6jn3">
                  {metric.label}
                </p>
              </article>)}
          </div>

          <article className="flex min-h-0 flex-1 flex-col rounded-2xl border border-[var(--brand)]/10 bg-white p-5 shadow-sm" data-id="element-243" data-mp="3ts3ffvtvuqql">
            <div className="flex items-center gap-2 text-[var(--brand)]" data-id="element-244" data-mp="2e7e7ee1e4hj">
              <EyeIcon className="h-5 w-5" aria-hidden="true" data-id="element-245" data-mp="313fr1rdu7l8t" />
              <h3 className="text-xs font-extrabold uppercase tracking-[0.16em]" data-id="element-246" data-mp="3111vzij5q919">
                Leadership can see
              </h3>
            </div>
            <ul className="mt-4 flex flex-1 flex-col justify-around gap-3" data-id="element-247" data-mp="2yizudocsn9iv">
              {LEADERSHIP_INSIGHTS.map(insight => <li key={insight} className="flex gap-3 text-xs font-semibold leading-snug text-[var(--fg)]" data-id="element-248" data-mp="2tkerfh65qbqq">
                  <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--brand)]" data-id="element-249" data-mp="2omn1gsp2ozp6" />
                  {insight}
                </li>)}
            </ul>
            <div className="mt-4 flex items-start gap-3 border-t border-black/5 pt-4" data-id="element-250" data-mp="2flf2e8eqfuu1">
              <RefreshCwIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--brand)]" aria-hidden="true" data-id="element-251" data-mp="2asdlxvyvnn8c" />
              <p className="text-xs leading-snug text-[var(--muted)]" data-id="element-252" data-mp="25ukdewa3qh4w">
                The view closes the loop: portfolio gap → calendar priority → campaign → new evidence.
              </p>
            </div>
          </article>
        </motion.aside>
      </div>
    </InteriorSlide>;
}